// Environment service to access runtime environment variables
declare global {
  interface Window {
    env?: {
      PUBLIC_URL?: string;
      REACT_APP_API_BASE_URL?: string;
      REACT_APP_MICROFLOW_REST_URL?: string;
      REACT_APP_FILE_SERVICE_URL?: string;
    };
  }
}

class EnvService {
  private static instance: EnvService;

  private constructor() {}

  public static getInstance(): EnvService {
    if (!EnvService.instance) {
      EnvService.instance = new EnvService();
    }
    return EnvService.instance;
  }

  /**
   * Get environment variable value
   * Priority: window.env (runtime) > process.env (build time) > default
   */
  public get(key: string, defaultValue: string = ""): string {
    // First try runtime environment (from Docker)
    if (window.env && window.env[key as keyof typeof window.env]) {
      return window.env[key as keyof typeof window.env] || defaultValue;
    }

    // Fallback to build-time environment
    if (process.env[key]) {
      return process.env[key] || defaultValue;
    }

    return defaultValue;
  }

  /**
   * Get API base URL
   */
  public getApiBaseUrl(): string {
    return this.get(
      "REACT_APP_API_BASE_URL",
      "http://localhost:8081/ods-service"
    );
  }

  public getApiFileUrl(): string {
    return this.get(
      "REACT_APP_FILE_SERVICE_URL",
      "https://neb.bb.go.th/FileService"
    );
  }

  /**
   * Get Microflow REST URL
   */
  public getMicroflowRestUrl(): string {
    return this.get(
      "REACT_APP_MICROFLOW_REST_URL",
      "https://neb.bb.go.th/MicroflowRest/v2/DoAction"
    );
  }

  /**
   * Get public URL
   */
  public getPublicUrl(): string {
    return this.get("PUBLIC_URL", "/ods-platform");
  }

  /**
   * Check if running in Docker environment
   */
  public isDockerEnvironment(): boolean {
    return window.env !== undefined;
  }

  /**
   * Get all environment variables
   */
  public getAllEnv(): Record<string, string> {
    const env: Record<string, string> = {};

    // Runtime environment variables
    if (window.env) {
      Object.entries(window.env).forEach(([key, value]) => {
        if (value) {
          env[key] = value;
        }
      });
    }

    // Build-time environment variables (fallback)
    Object.entries(process.env).forEach(([key, value]) => {
      if (key.startsWith("REACT_APP_") || key === "PUBLIC_URL") {
        if (value && !env[key]) {
          env[key] = value;
        }
      }
    });

    return env;
  }
}

export default EnvService.getInstance();
